import { useEffect } from 'react'
import {
  nextParcelleResult,
  previousParcelleResult,
  scrollParcelleToTop,
} from 'redux/features/parcelle/slice'
import {
  makeSelectParcelles,
  makeSelectSelectedParcelleIndex,
} from 'redux/features/parcelle/selectors'
import { useAppSelector, useAppDispatch } from 'redux/hooks'
import { calculBounds } from 'utils/calculBounds'
import geoJsonObjectToCoordinates from 'utils/geoJsonObjectToCoordinates'
import useGoogleMap from './useGoogleMap'

interface ReturnProps {
  selectedParcelleIndex: number | null
  isNextAvailable: boolean
  isPreviousAvailable: boolean
  nextParcelle: () => void
  previousParcelle: () => void
}

const useParcelleNavigation = (): ReturnProps => {
  const parcelles = useAppSelector(makeSelectParcelles)
  const selectedParcelleIndex = useAppSelector(makeSelectSelectedParcelleIndex)
  const dispatch = useAppDispatch()
  const map = useGoogleMap()

  const nextParcelle = () => {
    map?.getStreetView().setVisible(false)
    dispatch(nextParcelleResult())
    dispatch(scrollParcelleToTop())
  }
  const previousParcelle = () => {
    map?.getStreetView().setVisible(false)
    dispatch(previousParcelleResult())
    dispatch(scrollParcelleToTop())
  }

  const handleKeyPress = (ev: KeyboardEvent) => {
    if (ev.key === 'ArrowRight') nextParcelle()
    if (ev.key === 'ArrowLeft') previousParcelle()
  }

  useEffect(() => {
    if (selectedParcelleIndex !== null) {
      const bounds = calculBounds(
        geoJsonObjectToCoordinates(parcelles[selectedParcelleIndex].geographie)
      )
      map?.fitBounds(bounds)
      map?.panToBounds(bounds)
    }
  }, [selectedParcelleIndex, map, parcelles])

  useEffect(() => {
    if (selectedParcelleIndex === null) return undefined
    document.addEventListener('keydown', handleKeyPress)
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  })

  return {
    selectedParcelleIndex,
    isNextAvailable:
      selectedParcelleIndex === null ||
      selectedParcelleIndex < parcelles.length - 1,
    isPreviousAvailable:
      selectedParcelleIndex !== null && selectedParcelleIndex > 0,
    nextParcelle,
    previousParcelle,
  }
}

export default useParcelleNavigation
